import { useEffect, useRef, useState, type FunctionComponent, type SVGProps } from "react"
import { ArrowDown, ArrowRight, ArrowUp, ChevronRight } from "lucide-react"
import { useRouter } from "@tanstack/react-router"

import { cn } from "@/lib/utils"
import { useChat } from "@/features/chat"
import {
  advisorsList,
  AdvisorTrustedGuide,
  CreditsPurchaseModal,
  useAdvisorActions,
} from "@/features/advisors"
import { PersonalizedReports } from "@/features/today"

import AdvisorLoveLessonBg from "@/assets/img/compatibility/bgImageLoveLesson.webp"
import CompatibilityOrb from "@/assets/img/soulmate/report/compatibility-orb.png"
import BirthChartIcon from "@/assets/img/soulmate/report/birth-chart.png"
import MeetWhereIcon from "@/assets/svg/soulmate/report/meet-where.svg?react"
import MeetWhenIcon from "@/assets/svg/soulmate/report/meet-when.svg?react"
import ChartDecoration from "@/assets/svg/soulmate/report/bottom-decoration.svg?react"
import SketchPenIcon from "@/assets/svg/soulmate/report/sketch-pen.svg?react"
import ChipNotch from "@/assets/svg/soulmate/report/chip-notch.svg?react"
import BackArrow from "@/assets/svg/soulmate/report/back-arrow.svg?react"

import { planetIconFor, titleCase, zodiacIconFor } from "../config/soulmate-icons"
import type {
  DimensionLevel,
  SoulmateChart,
  SoulmateDimension,
  SoulmateReport,
  Zodiac,
} from "../types"
import { SoulmateContinueJourney } from "./soulmate-continue-journey"

type SvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>

interface SoulmateReportViewProps {
  content: SoulmateReport
}

/**
 * Native in-app Soulmate Sketch report (Figma node 3305-13600). Reuses the shared
 * in-app report building blocks — the "Personalized Reports" cross-sell, the
 * `AdvisorTrustedGuide` CTA and the advisors zodiac/planet glyphs — and only adds
 * the soulmate-specific chart, meeting and dimension pieces.
 */
export function SoulmateReportView({ content }: SoulmateReportViewProps) {
  const router = useRouter()
  const { userMainSubscriptionId } = useChat()
  const { isPaywallOpen, closePaywall, handleChatWithAdvisor } = useAdvisorActions()
  const name = content.soulmate.suggestedName

  const askAdvisor = () => handleChatWithAdvisor(advisorsList[9])

  // Sticky navbar: transparent over the portrait, solid once it's scrolled past.
  // A zero-height sentinel at the hero's bottom flips the state via an observer
  // whose top is inset by the 56px navbar.
  const [scrolled, setScrolled] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { rootMargin: "-56px 0px 0px 0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="mx-auto w-full pb-6">
      <SoulmateNavbar scrolled={scrolled} onBack={() => router.history.back()} />
      <Hero portraitUrl={content.portrait.assetUrl} />
      <div ref={sentinelRef} aria-hidden />

      <ChartSection content={content} onAsk={askAdvisor} />

      <ProseSection title="First Impression" text={content.firstImpression} />
      <ProseSection title="Why This Match Works" text={content.whyThisMatchWorks} />

      <PersonalizedReports />

      <ProseSection title="Their Love Energy" text={content.theirLoveEnergy} />
      <ProseSection title="You in This Connection" text={content.youInThisConnection} />

      <MeetingSection meeting={content.meeting} />

      <div className="mt-10 flex flex-col gap-10">
        <DimensionSection
          title="Trust"
          dimension={content.dimensions.trust}
          you={content.you}
          soulmate={content.soulmate}
          axis="sun"
        />
        <DimensionSection
          title="Love"
          dimension={content.dimensions.love}
          you={content.you}
          soulmate={content.soulmate}
          axis="moon"
        />
        <DimensionSection
          title="Emotion"
          dimension={content.dimensions.emotion}
          you={content.you}
          soulmate={content.soulmate}
          axis="ruling"
        />
      </div>

      <ProseSection title="Relationship Snapshot" text={content.relationshipSnapshot} />

      <div className="mt-10">
        <AdvisorTrustedGuide
          advisor={advisorsList[1]}
          onChat={handleChatWithAdvisor}
          bgImage={AdvisorLoveLessonBg}
          title={"Is it love\nor a lesson?"}
          subtitle="Explore the hidden energy between you"
          message={`Every connection has a deeper meaning. I can help you understand why ${name} appeared, what it may teach you, and whether it's worth opening your heart to.`}
          buttonLabel="Ask an advisor"
        />
      </div>

      <ValidationSection text={content.validation} />

      <div className="mt-10 px-4">
        <SoulmateContinueJourney />
      </div>

      <CreditsPurchaseModal
        isOpen={isPaywallOpen}
        onClose={closePaywall}
        userMainSubscriptionId={userMainSubscriptionId}
        variant="main-page"
      />
    </div>
  )
}

/* ── Sticky navbar ─────────────────────────────────────────────────────────── */

function SoulmateNavbar({ scrolled, onBack }: { scrolled: boolean; onBack: () => void }) {
  return (
    <div
      className={cn(
        // Sticks to the top; transparent over the portrait, dark-base once scrolled
        // past it (Figma node 3327-7456).
        "fixed inset-x-0 top-0 z-[60] mx-auto flex h-14 max-w-app items-center px-4 transition-colors duration-200",
        scrolled ? "bg-dark-base" : "bg-transparent",
      )}
    >
      <button
        type="button"
        aria-label="Back"
        onClick={onBack}
        className="flex size-8 items-center justify-center text-purple-light [&_svg]:size-6"
      >
        <BackArrow />
      </button>
      <h1 className="pointer-events-none absolute inset-x-0 text-center font-display text-[22px] font-medium leading-[30px] text-purple-light">
        Soulmate Sketch
      </h1>
    </div>
  )
}

/* ── Hero ──────────────────────────────────────────────────────────────────── */

function Hero({ portraitUrl }: { portraitUrl: string }) {
  return (
    // Reserve the portrait's aspect ratio (414×552 ≈ 133%) with padding-top and
    // lay the image in absolutely, so the page doesn't jump when it loads.
    <div data-slot="soulmate-hero" className="relative w-full pt-[133.33%]">
      <img
        src={portraitUrl}
        alt="Your soulmate sketch"
        className="absolute inset-0 size-full object-cover"
      />

      {/* Top scrim so the navbar stays legible over the portrait. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-dark-base/80 to-transparent" />
      {/* Bottom scrim so the chip reads and the portrait melts into the page. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-dark-base" />

      {/* "A sketch shaped by your reading" chip on the bottom scrim, with the
          little notch (Pipka) sitting on its top edge. */}
      <div className="absolute inset-x-0 bottom-5 flex flex-col items-center">
        <ChipNotch className="h-2 w-8" />
        <div className="flex items-center gap-1.5 rounded-2xl bg-purple-light/10 px-3 py-2">
          <SketchPenIcon className="size-4" />
          <span className="font-body text-xs text-purple-light/70">A sketch shaped by your reading</span>
        </div>
      </div>
    </div>
  )
}

/* ── Your Sketch & Match (chart) ───────────────────────────────────────────── */

function ChartSection({ content, onAsk }: { content: SoulmateReport; onAsk: () => void }) {
  const FirstIcon = zodiacIconFor[content.you.sunSign]
  const SecondIcon = zodiacIconFor[content.soulmate.sunSign]

  return (
    // The block sits directly under the hero (per Figma the Intro Summary abuts
    // the header) — its own py-6 is the only gap up to the heading.
    <div>
      {/* The whole block is a purple-light/10 card (radius 32, padding 24/16,
          gap 24) — per Figma "Intro Summary" (node 3305:15557). */}
      <div className="flex flex-col items-center gap-6 overflow-hidden rounded-[32px] bg-purple-light/10 px-4 py-6">
        <div className="flex w-full flex-col gap-2 text-center">
          <h2 className="font-display text-2xl font-medium text-purple-light">
            Your Sketch &amp; Match
          </h2>
          {/* Static subtitle (not the LLM matchDescription) — per Figma. */}
          <p className="font-body text-base text-purple-light/70">
            This is the face your reading revealed. Your signs show why this match may feel familiar.
          </p>
        </div>

        <div className="relative flex w-full items-center justify-between">
          {/* Bottom layer: dashed orbital rings, centred on the orb (272×386). */}
          <ChartDecoration className="pointer-events-none absolute left-1/2 top-1/2 h-[386px] w-[272px] -translate-x-1/2 -translate-y-1/2" />

          <ChartColumn label="YOU" chart={content.you} />
          <ChartColumn label="SOULMATE" chart={content.soulmate} />

          {/* Top layer: compatibility orb (170×170) with the score 22px above centre. */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 size-[170px] -translate-x-1/2 -translate-y-1/2">
            <img src={CompatibilityOrb} alt="" className="size-full" />
            <span className="absolute left-1/2 top-[calc(50%-22px)] -translate-x-1/2 font-display text-[32px] font-bold text-white drop-shadow-[0_0_12px_rgba(149,101,253,0.6)]">
              {content.compatibilityScore}%
            </span>
          </div>
        </div>

        <div className="h-px w-full bg-purple-light/10" />

        <div className="flex w-full items-center gap-3">
          <div className="flex items-center gap-0.5 rounded-full border border-purple-light/20 px-3 py-2.5 text-purple-light [&_svg]:size-6">
            <FirstIcon />
            <span className="text-purple-light/50">+</span>
            <SecondIcon />
          </div>
          <p className="flex-1 text-left font-body text-sm text-purple-light/70">
            Ask an advisor why this match reached {content.compatibilityScore}% for you
          </p>
          <button
            type="button"
            onClick={onAsk}
            aria-label="Ask an advisor"
            className="flex size-11 min-w-11 items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent text-white transition-opacity hover:opacity-80 [&_svg]:size-6"
          >
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  )
}

function ChartColumn({ label, chart }: { label: string; chart: SoulmateChart }) {
  return (
    <div className="relative flex w-[120px] flex-col items-start gap-4 rounded-2xl bg-dark-base px-2 pb-4 pt-3">
      <div className="flex w-full flex-col gap-3">
        <p className="w-full text-center font-display text-xs font-medium text-purple-light">
          {label}
        </p>
        <div className="h-px w-full bg-purple-light/10" />
      </div>
      <ChartCell type="Sun Sign" Icon={zodiacIconFor[chart.sunSign]} name={titleCase(chart.sunSign)} />
      <ChartCell type="Moon Sign" Icon={zodiacIconFor[chart.moonSign]} name={titleCase(chart.moonSign)} />
      <ChartCell
        type="Ruling Planet"
        Icon={planetIconFor[chart.rulingPlanet]}
        name={titleCase(chart.rulingPlanet)}
      />
    </div>
  )
}

function ChartCell({ type, Icon, name }: { type: string; Icon: SvgComponent; name: string }) {
  return (
    <div className="flex h-[117px] w-full flex-col items-center justify-center gap-1">
      <span className="flex size-[72px] items-center justify-center rounded-full bg-gradient-to-b from-purple-light/40 to-main-purple/20 text-purple-light [&_svg]:size-9">
        <Icon />
      </span>
      <div className="flex w-full flex-col">
        <p className="w-full truncate text-center font-body text-sm text-purple-light/70">{type}</p>
        <p className="w-full text-center font-display text-base font-medium text-purple-light">
          {name}
        </p>
      </div>
    </div>
  )
}

/* ── Reusable prose block ──────────────────────────────────────────────────── */

function ProseSection({ title, text }: { title: string; text: string }) {
  const paragraphs = text.split(/\n{2,}/).filter(Boolean)
  return (
    <section className="mt-10 px-4">
      <h3 className="font-display text-2xl font-medium text-purple-light">{title}</h3>
      <div className="mt-4 flex flex-col gap-3">
        {paragraphs.map((paragraph, i) => (
          <p key={i} className="font-body text-base text-purple-light/70">
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  )
}

/* ── Where & When You May Meet ─────────────────────────────────────────────── */

function MeetingSection({ meeting }: { meeting: SoulmateReport["meeting"] }) {
  return (
    <section className="mt-10 flex flex-col gap-6">
      <div className="flex flex-col gap-2 px-4">
        <h3 className="font-display text-2xl font-medium text-purple-light">
          Where &amp; When You May Meet
        </h3>
        <p className="font-body text-base text-purple-light/70">{meeting.intro}</p>
      </div>

      <div className="flex snap-x gap-4 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <MeetingCard
          Icon={MeetWhereIcon}
          title="Where You May Meet"
          items={meeting.where}
          bulletClass="bg-peach"
        />
        <MeetingCard
          Icon={MeetWhenIcon}
          title="When It May Happen"
          items={meeting.when}
          bulletClass="bg-cyan"
        />
      </div>
    </section>
  )
}

function MeetingCard({
  Icon,
  title,
  items,
  bulletClass,
}: {
  Icon: SvgComponent
  title: string
  items: readonly string[]
  bulletClass: string
}) {
  return (
    <div className="flex w-[320px] shrink-0 snap-start flex-col gap-4 rounded-3xl bg-gradient-to-bl from-purple-light/10 to-purple-light/5 px-4 pb-6 pt-4">
      <Icon className="size-14" />
      <p className="font-display text-xl font-medium text-purple-light">{title}</p>
      <ul className="flex flex-col gap-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-3">
            <span className={cn("size-2 shrink-0 rounded-full", bulletClass)} />
            <span className="font-body text-sm text-purple-light/70">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ── Dimension (Trust / Love / Emotion) ────────────────────────────────────── */

const LEVEL_META: Record<DimensionLevel, { label: string; className: string; Icon: SvgComponent }> = {
  high: { label: "HIGH", className: "bg-green/20 text-green", Icon: ArrowUp },
  medium: { label: "MEDIUM", className: "bg-gold/20 text-gold", Icon: ArrowRight },
  low: { label: "LOW", className: "bg-peach/20 text-peach", Icon: ArrowDown },
}

/** Fixed pink→green palette; the fill is clipped to `percent` so the visible
 *  right edge lands on that point of the scale (matches the Figma masked bar). */
const BAR_GRADIENT =
  "linear-gradient(90deg, #ff537f 0%, #ff6d4b 25%, #ff9b00 50%, #ded500 75%, #7fe718 100%)"

function DimensionSection({
  title,
  dimension,
  you,
  soulmate,
  axis,
}: {
  title: string
  dimension: SoulmateDimension
  you: SoulmateChart
  soulmate: SoulmateChart
  axis: "sun" | "moon" | "ruling"
}) {
  const level = LEVEL_META[dimension.level]
  const LevelIcon = level.Icon
  const percent = Math.min(100, Math.max(0, dimension.percent))

  const youValue = axis === "sun" ? you.sunSign : axis === "moon" ? you.moonSign : you.rulingPlanet
  const soulmateValue =
    axis === "sun"
      ? soulmate.sunSign
      : axis === "moon"
        ? soulmate.moonSign
        : soulmate.rulingPlanet
  const YouIcon =
    axis === "ruling" ? planetIconFor[you.rulingPlanet] : zodiacIconFor[youValue as Zodiac]
  const SoulmateIcon =
    axis === "ruling"
      ? planetIconFor[soulmate.rulingPlanet]
      : zodiacIconFor[soulmateValue as Zodiac]
  const signLabel = axis === "sun" ? "Sun Sign" : axis === "moon" ? "Moon Sign" : "Ruling Planet"

  return (
    <section className="flex flex-col gap-6 px-4">
      <div className="flex flex-col gap-4">
        <h3 className="font-display text-2xl font-medium text-purple-light">{title}</h3>

        <div className="flex w-full flex-col gap-4 overflow-hidden rounded-[24px] bg-purple-light/10 p-4">
          {/* Compatibility % + level badge + comparison bar. */}
          <div className="flex w-full flex-col items-center gap-2">
            <div className="flex w-full items-center justify-between">
              <p className="font-display text-xl font-medium text-purple-light">
                {dimension.percent}% compatibility
              </p>
              <span
                className={cn(
                  "flex items-center gap-1 rounded-[13px] px-2 py-1.5 font-body text-[10px] font-medium uppercase [&_svg]:size-3",
                  level.className,
                )}
              >
                <LevelIcon />
                {level.label}
              </span>
            </div>

            <div className="relative h-3 w-full overflow-hidden rounded-full bg-purple-light/10">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: BAR_GRADIENT,
                  clipPath: `inset(0 ${100 - percent}% 0 0 round 9999px)`,
                }}
              />
            </div>
          </div>

          {/* You ↔ Soulmate sign pair. */}
          <div className="flex w-full items-center gap-3">
            <SignSide Icon={YouIcon} badge="YOU" typeLabel={signLabel} name={titleCase(youValue)} />
            <div className="h-px flex-1 bg-purple-light/20" />
            <SignSide
              Icon={SoulmateIcon}
              badge="SOULMATE"
              typeLabel={signLabel}
              name={titleCase(soulmateValue)}
              reversed
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {dimension.text.split(/\n{2,}/).filter(Boolean).map((paragraph, i) => (
          <p key={i} className="font-body text-base text-purple-light/70">
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  )
}

function SignSide({
  Icon,
  badge,
  typeLabel,
  name,
  reversed = false,
}: {
  Icon: SvgComponent
  badge: string
  typeLabel: string
  name: string
  reversed?: boolean
}) {
  // Gradient icon pill with a pale YOU/SOULMATE badge overlapping its base.
  const iconBlock = (
    <div className="flex shrink-0 flex-col items-center">
      <div className="-mb-2 flex rounded-[34px] bg-gradient-to-b from-purple-light/40 to-main-purple/20 px-3 pb-3.5 pt-2.5 text-purple-light [&_svg]:size-9">
        <Icon />
      </div>
      <span className="flex w-full items-center justify-center rounded-[13px] bg-purple-light/90 py-1 font-body text-[8px] font-bold uppercase text-dark-base">
        {badge}
      </span>
    </div>
  )
  const textBlock = (
    <div className={cn("flex flex-col justify-center", reversed ? "items-end text-right" : "items-start")}>
      <span className="font-body text-sm text-purple-light/70">{typeLabel}</span>
      <span className="font-display text-base font-medium text-purple-light">{name}</span>
    </div>
  )
  return (
    <div className="flex min-w-[96px] items-center justify-center gap-3">
      {reversed ? textBlock : iconBlock}
      {reversed ? iconBlock : textBlock}
    </div>
  )
}

/* ── Based on Your Birth Chart ─────────────────────────────────────────────── */

function ValidationSection({ text }: { text: string }) {
  return (
    <section className="mt-10 px-4">
      <h3 className="font-display text-2xl font-medium text-purple-light">
        Based on Your Birth Chart
      </h3>
      <div className="mt-3 flex items-center gap-4">
        <img src={BirthChartIcon} alt="" className="size-14 shrink-0" />
        <p className="font-body text-base text-purple-light/70">{text}</p>
      </div>
    </section>
  )
}
